import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import permissionManager from '../utils/permissionManager';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredRoles = null,
  requiredPermission = null, 
  requiredPermissions = null,
  requireAll = false,
  redirectTo = '/login',
  unauthorizedRedirect = '/dashboard'
}) => {
  const location = useLocation();
  const isAuthenticated = permissionManager.isAuthenticated();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  const checkRoleAccess = () => {
    if (requiredRole) {
      return permissionManager.hasRole(requiredRole);
    }
    if (requiredRoles && Array.isArray(requiredRoles)) {
      return permissionManager.hasAnyRole(requiredRoles);
    }
    return true;
  };

  // Check permission-based access
  const checkPermissionAccess = () => {
    if (requiredPermission) {
      return permissionManager.hasPermission(requiredPermission);
    }
    if (requiredPermissions && Array.isArray(requiredPermissions)) {
      return requireAll 
        ? permissionManager.hasAllPermissions(requiredPermissions)
        : permissionManager.hasAnyPermission(requiredPermissions);
    }
    return true;
  };

  // Check route-specific access
  const canAccessCurrentRoute = permissionManager.canAccessRoute(location.pathname);

  // Determine if user has access
  const hasRoleAccess = checkRoleAccess();
  const hasPermissionAccess = checkPermissionAccess();
  const hasAccess = hasRoleAccess && hasPermissionAccess && canAccessCurrentRoute;

  // If user doesn't have access, redirect to unauthorized page or dashboard
  if (!hasAccess) {
    // Don't redirect if already on the unauthorized redirect page to prevent infinite loops
    if (location.pathname !== unauthorizedRedirect) {
      return <Navigate to={unauthorizedRedirect} replace />;
    }
  }

  return children;
};

// Convenience components for common protection patterns
export const SuperAdminRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRole="super_admin" {...props}>
    {children}
  </ProtectedRoute>
);

export const ManagerOrAboveRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRoles={['super_admin', 'manager']} {...props}>
    {children}
  </ProtectedRoute>
);

export const TeamMemberRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredRole="team" {...props}>
    {children}
  </ProtectedRoute>
);

// Permission-based route protection
export const DashboardRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredPermission="view_dashboard" {...props}>
    {children}
  </ProtectedRoute>
);

export const AutomationRunsRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredPermission="view_automation_runs" {...props}>
    {children}
  </ProtectedRoute>
);

export const LogsRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredPermission="view_logs" {...props}>
    {children}
  </ProtectedRoute>
);

export const UserManagementRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredPermissions={['view_users', 'manage_users']} {...props}>
    {children}
  </ProtectedRoute>
);

export const SettingsRoute = ({ children, ...props }) => (
  <ProtectedRoute requiredPermission="manage_settings" {...props}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;